import { AuthRepository } from "../../domain/repositories/AuthRepository";
import {
  AuthResultEntity,
  LoginCredentials,
} from "../../domain/entities/AuthResult";
import { UserEntity } from "../../domain/entities/User";
import { CompanyEntity } from "../../domain/entities/Company";
import {
  loginApi,
  adminLoginApi,
  logoutApi,
  updateZohoTokenApi,
} from "../api/authApi";
import {
  storeToken,
  getToken,
  storeUser,
  getUser,
  updateUser as updateStoredUser,
  storeCompany,
  getCompany,
  clearAllAuthData,
  isAuthenticated as checkIsAuthenticated,
  storeZohoTokens,
} from "../../../../shared/data/storage/authStorage";
import { AuthMapper } from "../mappers/AuthMapper";

/**
 * Auth Repository Implementation
 * Implements the AuthRepository interface from the domain layer
 * Coordinates between API calls and local storage
 */
export class AuthRepositoryImpl implements AuthRepository {
  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<AuthResultEntity> {
    // Call API
    const response = await loginApi({
      email: credentials.email,
      password: credentials.password,
    });

    // Map to domain entities
    const authResult = AuthMapper.toAuthResult(response);

    // Store in localStorage
    storeToken(authResult.token);
    storeUser(authResult.user);
    storeZohoTokens(authResult.user.zohoRefreshToken || "");
    storeCompany(authResult.company);

    return authResult;
  }

  /**
   * Admin login
   */
  async adminLogin(credentials: LoginCredentials): Promise<AuthResultEntity> {
    // Call API (validates admin role)
    const response = await adminLoginApi({
      email: credentials.email,
      password: credentials.password,
    });

    // Map to domain entities
    const authResult = AuthMapper.toAuthResult(response);
    console.log("Auth result:", authResult);
    // Store in localStorage
    storeToken(authResult.token);
    storeUser(authResult.user);
    storeZohoTokens(authResult.user.zohoRefreshToken || "");
    storeCompany(authResult.company);

    return authResult;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    // Call API (don't throw if it fails)
    await logoutApi();

    // Clear all local storage
    clearAllAuthData();
  }

  /**
   * Update Zoho refresh token
   */
  async updateZohoToken(refreshToken: string): Promise<void> {
    // Call API
    await updateZohoTokenApi(refreshToken);

    // Update user in localStorage
    const currentUser = getUser();
    if (currentUser) {
      updateStoredUser({ zohoRefreshToken: refreshToken });
    }
  }

  /**
   * Get stored token
   */
  getStoredToken(): string | null {
    return getToken();
  }

  /**
   * Get stored user
   */
  getStoredUser(): UserEntity | null {
    return getUser();
  }

  /**
   * Get stored company
   */
  getStoredCompany(): CompanyEntity | null {
    return getCompany();
  }

  /**
   * Check if authenticated
   */
  isAuthenticated(): boolean {
    return checkIsAuthenticated();
  }
}
